import type React from "react";
import type { AirDetails, Country } from "../../types";
import type { OrderFormValues } from "../../types/cmrpreview";

type CountryCode = { _id: string } | string | undefined | null;

/**
 * Live Air Waybill preview — the AIR counterpart of CmrPreview / BolPreview.
 * Reads the shared order values (shipper = pickup, consignee = delivery, cargo,
 * client) plus the air-specific block. Air has no remark rows.
 */
export const AwbPreview: React.FC<{
  shipment: OrderFormValues;
  airDetails?: AirDetails;
  countries: Country[];
}> = ({ shipment, airDetails, countries }) => {
  const countryOptions = (countries ?? []).flat().map((country) => ({
    value: country._id,
    label: country.name,
  }));

  const getCountryName = (codeOrName: CountryCode): string => {
    if (!codeOrName) return "";
    const code = typeof codeOrName === "string" ? codeOrName : codeOrName._id;
    if (!code) return "";
    const found = countryOptions.find(
      (c) => c.value?.toLowerCase?.() === code.toLowerCase?.(),
    );
    return found?.label || "";
  };

  const cargo = shipment?.cargoItems ?? [];
  const totalPackages = cargo.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
  const totalWeight = cargo.reduce((s, i) => s + (Number(i.weightKg) || 0), 0);
  const totalVolume = cargo.reduce((s, i) => s + (Number(i.volumeM3) || 0), 0);
  const totalChargeable = cargo.reduce(
    (s, i) => s + (Number((i as any).chargeableWeight) || 0),
    0,
  );

  const fmtDate = (d?: string) => (d ? d.split("T")[0] : "");

  // AWB document block. CHGS prints the IATA payment code (PP / CC).
  const awb = airDetails?.awb;
  const chgs = (term?: string) =>
    term === "Prepaid" ? "PP (Prepaid)" : term === "Collect" ? "CC (Collect)" : "";
  const shcLine = (awb?.specialHandlingCodes ?? []).filter(Boolean).join(", ");
  const remarkLines = (awb?.remarks ?? []).filter(Boolean).join("\n");
  const notify = awb?.notifyParty;
  const notifyLines = [
    notify?.companyName,
    notify?.contactPerson,
    notify?.email,
    notify?.phone,
  ]
    .filter(Boolean)
    .join("\n");
  const awbNumber = awb?.awbNumber || airDetails?.forwarder?.hawbNumber || "";
  const issueDate = fmtDate(awb?.awbDate) || fmtDate(airDetails?.mawbDate);
  const issuingAgent = [awb?.issuingAgentName, awb?.issuingAgentCity]
    .filter(Boolean)
    .join(", ");
  const carrierFlight =
    [airDetails?.airlineName, airDetails?.flightNumber].filter(Boolean).join(" / ") ||
    "";

  const Party: React.FC<{ party?: OrderFormValues["pickup"] }> = ({ party }) => (
    <div className="text-xs">
      <div className="font-medium">{party?.companyName || ""}</div>
      <div>{party?.address?.street?.split(",")[0] || ""}</div>
      <div>
        {[party?.address?.postalCode, party?.address?.city, party?.address?.state]
          .filter(Boolean)
          .join(" ")}
      </div>
      <div>{getCountryName(party?.address?.country)}</div>
      {party?.registerationCode && <div>Reg.nr {party.registerationCode}</div>}
      {party?.emailAddress && <div>{party.emailAddress}</div>}
    </div>
  );

  const Cell: React.FC<{ label: string; children?: React.ReactNode; className?: string }> = ({
    label,
    children,
    className,
  }) => (
    <div className={`border border-gray-400 p-2 ${className ?? ""}`}>
      <div className="font-semibold text-[10px] uppercase text-gray-500 mb-1">
        {label}
      </div>
      <div className="text-xs whitespace-pre-line">{children}</div>
    </div>
  );

  return (
    <div className="space-y-1 break-words">
      {/* Header */}
      <div className="text-center mb-3 border-b pb-2">
        <h2 className="font-bold text-sm">AIR WAYBILL</h2>
        <p className="text-[10px]">Air Waybill · Non-negotiable</p>
      </div>

      {/* AWB No. · Issuing agent */}
      <div className="grid grid-cols-2 gap-px">
        <Cell label="Air Waybill No.">
          <span className="font-medium">{awbNumber}</span>
        </Cell>
        <Cell label="Issuing Carrier's Agent Name & City">
          {issuingAgent || airDetails?.forwarder?.companyName || ""}
        </Cell>
      </div>

      {/* Shipper · Consignee */}
      <div className="grid grid-cols-2 gap-px">
        <Cell label="Shipper Name & Address" className="min-h-[90px]">
          <Party party={shipment?.pickup} />
        </Cell>
        <Cell label="Consignee Name & Address" className="min-h-[90px]">
          <Party party={shipment?.delivery} />
        </Cell>
      </div>

      {/* Notify party · MAWB reference */}
      <div className="grid grid-cols-2 gap-px">
        <Cell label="Notify Party" className="min-h-[70px]">
          {notifyLines}
        </Cell>
        <Cell label="MAWB Reference">{airDetails?.mawbNumber || ""}</Cell>
      </div>

      {/* Route · carrier/flight */}
      <div className="grid grid-cols-3 gap-px">
        <Cell label="Airport of Departure">{airDetails?.airportOfDeparture || ""}</Cell>
        <Cell label="By (Carrier) / Flight">
          {carrierFlight}
          {fmtDate(airDetails?.etd) ? `\n${fmtDate(airDetails?.etd)}` : ""}
        </Cell>
        <Cell label="Airport of Destination">{airDetails?.airportOfArrival || ""}</Cell>
      </div>

      {/* Charges */}
      <div className="grid grid-cols-4 gap-px">
        <Cell label="Currency">{awb?.currency || ""}</Cell>
        <Cell label="CHGS Code (Freight)">{chgs(awb?.freightPaymentTerms)}</Cell>
        <Cell label="Declared Value / Carriage">
          {awb?.declaredValueCarriage || "NVD"}
        </Cell>
        <Cell label="Declared Value / Customs">
          {awb?.declaredValueCustoms || "NCV"}
        </Cell>
      </div>

      {/* Cargo details */}
      <div className="grid grid-cols-3 gap-px">
        <Cell label="No. & Kind of Packages · Description of Goods" className="col-span-2 min-h-[90px]">
          {totalPackages > 0 ? `${totalPackages} package(s) said to contain:\n` : ""}
          {cargo
            .map((i) =>
              [[i.quantity, i.type].filter(Boolean).join(" "), i.description]
                .filter(Boolean)
                .join(" — "),
            )
            .filter(Boolean)
            .map((l) => `- ${l}`)
            .join("\n")}
        </Cell>
        <Cell label="Gross / Chargeable Weight" className="min-h-[90px]">
          {totalWeight > 0 ? `${totalWeight.toLocaleString("en-US")} kg` : "—"}
          {totalChargeable > 0
            ? `\nChg. ${totalChargeable.toLocaleString("en-US")} kg`
            : ""}
          {totalVolume > 0 ? `\n${totalVolume.toLocaleString("en-US")} m³` : ""}
        </Cell>
      </div>

      {/* Other charges · Special handling */}
      <div className="grid grid-cols-2 gap-px">
        <Cell label="Other Charges">{chgs(awb?.otherChargesPayment)}</Cell>
        <Cell label="Special Handling Codes (SHC)">{shcLine}</Cell>
      </div>

      {/* Handling information / remarks */}
      <Cell label="Handling Information / Remarks" className="min-h-[60px]">
        {remarkLines}
      </Cell>

      {/* Footer · place of execution · date · signature */}
      <div className="grid grid-cols-3 gap-px">
        <Cell label="Place of Execution">{awb?.issuingAgentCity || ""}</Cell>
        <Cell label="Date">{issueDate}</Cell>
        <Cell label="Signature (Issuing Carrier / Agent)">
          {awb?.issuingAgentName || airDetails?.forwarder?.companyName || ""}
        </Cell>
      </div>
    </div>
  );
};

export default AwbPreview;
